import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { fmtEur } from '../../lib/utils';
import { toast } from '../../stores/toast.store';
import { celebrate } from '../../stores/moment.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';

/**
 * Azioni condivise sui documenti contabili (fatture + preventivi).
 *
 * Estratte da UnifiedTable (InvoicesPage) perché servono in più viste:
 * la tabella documenti E lo scadenziario offrono le stesse azioni contestuali
 * ('Emetti la fattura', 'Segna incassata', 'Crea la fattura', 'Reinvia') senza duplicare le mutation.
 *
 * Ogni mutation invalida le query interessate e gestisce toast/celebrate;
 * i consumer chiamano solo `.mutate(...)` e usano `.isPending` per il disable.
 */

interface ClientContact { id: string; fullName: string; email: string | null; isPrimary: boolean; }

export interface ResendQuoteArgs {
  id: string;
  clientId: string;
  number: string | null;
  title: string | null;
}

export interface MarkInvoicePaidArgs {
  id: string;
  /** Importo per la celebrazione e il toast: la rata, o la fattura intera. */
  total: number;
  /** La rata da chiudere; senza, tutte le rate aperte della fattura. */
  paymentId?: string | null;
  paidDate?: string;
  method?: string;
  accountId?: string | null;
  bankTransactionId?: string | null;
  localPaidNote?: string | null;
  /** "Fattura 2026/12 · La Rosa" per il toast. */
  label?: string;
}

export interface SendInvoiceReminderArgs {
  id: string;
  clientId: string;
  number: string | null;
}

export function useDocActions() {
  const qc = useQueryClient();
  const navigate = useNavigate();

  /** Emissione locale (senza FattureInCloud). */
  const issueInvoiceLocal = useMutation({
    mutationFn: (id: string) => api.post(`/invoices/${id}/issue`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoices'] }); toast.ok('Fattura emessa con la numerazione interna'); },
    onError: (e: any) => toast.alert('Fattura non emessa', e?.message ?? 'Riprova'),
  });

  /** Emissione fiscale: push su FattureInCloud. */
  const pushInvoiceFic = useMutation({
    mutationFn: (id: string) => api.post(`/invoices/${id}/push-to-fic`),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['invoices'] }); toast.ok('Fattura emessa su FattureInCloud'); },
    onError: (e: any) => toast.alert('Fattura non emessa su FattureInCloud', e?.message ?? 'Riprova'),
  });

  /**
   * Incasso di una fattura, con celebrazione dell'importo.
   *
   * Chiude le RATE, non solo la fattura (POST /invoices/:id/mark-paid, vedi
   * invoices.routes.ts): con `paymentId` quella rata sola, senza tutte le rate
   * aperte. I campi del popover (data, metodo, bonifico, nota) sono facoltativi:
   * Oggi e il dettaglio fattura chiamano ancora con il solo id. È una
   * registrazione in Masamune OS: su FattureInCloud non si scrive.
   */
  const markInvoicePaid = useMutation({
    mutationFn: ({ id, paymentId, paidDate, method, accountId, bankTransactionId, localPaidNote }: MarkInvoicePaidArgs) =>
      api.post<{ closedPaymentIds?: string[] }>(`/invoices/${id}/mark-paid`, {
        ...(paymentId ? { paymentId } : {}),
        ...(paidDate ? { paidDate } : {}),
        ...(method ? { method } : {}),
        ...(accountId ? { accountId } : {}),
        ...(bankTransactionId ? { bankTransactionId } : {}),
        ...(localPaidNote ? { localPaidNote } : {}),
      }),
    onSuccess: (r, vars) => {
      const invalidate = () => {
        qc.invalidateQueries({ queryKey: ['invoices'] });
        qc.invalidateQueries({ queryKey: ['invoice', vars.id] });
        // Le rate chiuse spariscono da Fatture da incassare, Oggi e Situazione
        qc.invalidateQueries({ queryKey: ['admin'] });
      };
      invalidate();
      celebrate({ title: 'Fattura incassata', amount: vars.total, tone: 'ok' });
      // Undo alla Gmail: 6 secondi per annullare un incasso registrato per sbaglio.
      const riaprire = r?.closedPaymentIds ?? [];
      toast.undo('Incasso registrato', {
        description: `${vars.label ? `${vars.label} · ` : ''}€ ${fmtEur(vars.total)}`,
        onUndo: () => {
          api.post(`/invoices/${vars.id}/unmark-paid`, riaprire.length ? { paymentIds: riaprire } : undefined)
            .then(invalidate)
            .catch((e: any) => toast.alert('Incasso non annullato', e?.message ?? 'Riprova'));
        },
      });
    },
    onError: (e: any) => toast.alert('Incasso non registrato', e?.message ?? 'Riprova'),
  });

  /** Conversione preventivo firmato → fattura, con navigazione alla fattura creata. */
  const convertQuote = useMutation({
    mutationFn: ({ id }: { id: string; total: number }) => api.post<{ invoiceId: string }>(`/quotes/${id}/convert-to-invoice`),
    onSuccess: (res, vars) => {
      qc.invalidateQueries({ queryKey: ['quotes'] });
      qc.invalidateQueries({ queryKey: ['invoices'] });
      celebrate({ title: 'Preventivo diventato fattura', amount: vars.total, subtitle: 'La fattura è in bozza: controllala ed emettila', tone: 'product' });
      navigate(`/invoices/${res.invoiceId}`);
    },
    onError: (e: any) => toast.alert('Fattura non creata dal preventivo', e?.message ?? 'Riprova'),
  });

  /**
   * Reinvio rapido di un preventivo già inviato: recupera il contatto del
   * cliente (primario, o il primo con email), chiede conferma e rispedisce
   * l'email con il link firma. Stesso endpoint dell'invio iniziale: il token
   * di firma esistente viene riusato, quindi il link resta lo stesso.
   * Ritorna `null` se l'utente annulla la conferma (nessun toast).
   */
  const resendQuote = useMutation({
    mutationFn: async ({ id, clientId, number, title }: ResendQuoteArgs) => {
      const contacts = await api.get<ClientContact[]>(`/clients/${clientId}/contacts`).catch(() => [] as ClientContact[]);
      const dest = contacts.find((c) => c.isPrimary && c.email) ?? contacts.find((c) => c.email);
      if (!dest?.email) {
        throw new Error('Nessun contatto con email per questo cliente: apri il preventivo per scegliere il destinatario.');
      }
      const ok = await confirmDialog({
        title: 'Reinviare il preventivo?',
        body: `${number ?? 'Il preventivo'} verrà rispedito a ${dest.email} con il link per la firma.`,
        confirmLabel: 'Reinvia',
        icon: 'send',
      });
      if (!ok) return null;
      await api.post(`/quotes/${id}/send`, {
        to: dest.email,
        subject: title ?? `Preventivo ${number ?? ''}`.trim(),
        message: 'Ciao,\n\nti rimando il preventivo: trovi tutto nel link qui sotto, puoi visualizzarlo e firmarlo direttamente online.\n\nFammi sapere se hai dubbi o serve qualche aggiustamento.\n\nUn saluto',
      });
      return true;
    },
    onSuccess: (sent) => {
      if (!sent) return; // annullato dall'utente
      qc.invalidateQueries({ queryKey: ['quotes'] });
      toast.ok('Preventivo reinviato', 'Il cliente riceverà di nuovo l\'email con il link firma.');
    },
    onError: (e: any) => toast.alert('Preventivo non reinviato', e?.message ?? 'Riprova'),
  });

  /**
   * Sollecito di una fattura aperta (scadenziario azionabile).
   *
   * Riusa lo stesso pattern di `resendQuote`: recupera il contatto del cliente
   * (primario, o il primo con email), chiede conferma e rispedisce l'email.
   * Non esiste un endpoint dedicato `/remind`: si riusa l'invio email del
   * dettaglio fattura (`/invoices/:id/send-email-fic`), lo stesso usato dalla
   * voce "Invia email cliente". Quell'endpoint spedisce tramite FattureInCloud,
   * quindi richiede una fattura emessa su FIC: se non lo è, l'API risponde con
   * un errore parlante che finisce nel toast `onError` (nessun endpoint inventato).
   * Ritorna `null` se l'utente annulla la conferma (nessun toast).
   */
  const sendInvoiceReminder = useMutation({
    mutationFn: async ({ id, clientId, number }: SendInvoiceReminderArgs) => {
      const contacts = await api.get<ClientContact[]>(`/clients/${clientId}/contacts`).catch(() => [] as ClientContact[]);
      const dest = contacts.find((c) => c.isPrimary && c.email) ?? contacts.find((c) => c.email);
      if (!dest?.email) {
        throw new Error('Nessun contatto con email per questo cliente: apri la fattura per scegliere il destinatario.');
      }
      const ok = await confirmDialog({
        title: 'Sollecitare il pagamento?',
        body: `Verrà inviato un sollecito per ${number ?? 'la fattura'} a ${dest.email}.`,
        confirmLabel: 'Sollecita',
        icon: 'bell',
      });
      if (!ok) return null;
      await api.post(`/invoices/${id}/send-email-fic`, {
        to: dest.email,
        subject: `Sollecito pagamento ${number ?? ''}`.trim(),
        body: 'Ciao,\n\nti ricordo gentilmente che la fattura in allegato risulta ancora da saldare. Trovi tutti i dettagli per il pagamento nel documento.\n\nSe l\'hai già pagata, ignora pure questo messaggio.\n\nGrazie e a presto',
      });
      return true;
    },
    onSuccess: (sent) => {
      if (!sent) return; // annullato dall'utente
      qc.invalidateQueries({ queryKey: ['invoices'] });
      toast.ok('Sollecito inviato', 'Il cliente riceverà l\'email di promemoria pagamento.');
    },
    onError: (e: any) => toast.alert('Sollecito non inviato', e?.message ?? 'Riprova'),
  });

  return { issueInvoiceLocal, pushInvoiceFic, markInvoicePaid, convertQuote, resendQuote, sendInvoiceReminder };
}
